import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

function renderCenterLabel(label, progress) {
    const isFirstInfluencerDomain =
        typeof window !== "undefined" &&
        window.location.hostname.includes("1stinfluencer");
    return (
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
            <tspan
                x="50%"
                dy="-0.2em"
                fontSize="16"
                fill={isFirstInfluencerDomain ? "#0074e4" : "#fe60a1"}
            >
                {label}
            </tspan>
            <tspan
                x="50%"
                dy="1.2em"
                fontSize="14"
                fill={isFirstInfluencerDomain ? "#0074e4" : "#fe60a1"}
            >
                {progress}%
            </tspan>
        </text>
    );
}

export default function TaskDonutCharts({
    validatedProfile,
    engagePercent = 0,
    currentStars = 0, //make engage progress be currentStars if engagePercent is 0 and currentStars < 50
    activeTab, // <--- Pass in "engage" | "post" | "grow"
}) {
    // Determine color scheme based on hostname
    // (Be sure to guard for SSR: window may be undefined if running on server)
    const isFirstInfluencerDomain =
        typeof window !== "undefined" &&
        window.location.hostname.includes("1stinfluencer");

    // Gradient colors for the active portion
    const gradientStart = isFirstInfluencerDomain ? "#0074e4" : "#fe60a1";
    const gradientEnd = isFirstInfluencerDomain ? "#67aaf1" : "#ff8765";

    // For the active ring leftover portion
    const leftoverActiveColor = isFirstInfluencerDomain ? "#d0e8fb" : "#fde6e6";

    // Calculate GROW donut progress
    const growTotal = validatedProfile?.[0]?.connectionsWeeklyLimit ?? 100;
    const growCurrent =
        validatedProfile?.[0]?.connectionsWeeklyLimitCounter ?? 0;
    const growProgress = Math.min(
        Math.round((growCurrent / growTotal) * 100),
        100
    );

    // Calculate POST donut progress
    let postProgress = 0;
    if (validatedProfile?.[0]?.lastPostDate) {
        const lastPostDate = validatedProfile[0].lastPostDate;
        const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
        postProgress = new Date(lastPostDate).getTime() > oneDayAgo ? 100 : 0;
    }

    // ENGAGE donut progress

    // Prepare data sets
    const engageData = [
        { name: "Progress", value: engagePercent },
        { name: "Remaining", value: 100 - engagePercent },
    ];
    const postData = [
        { name: "Progress", value: postProgress },
        { name: "Remaining", value: 100 - postProgress },
    ];
    const growData = [
        { name: "Progress", value: growProgress },
        { name: "Remaining", value: 100 - growProgress },
    ];

    return (
        <div key={activeTab} style={{ width: 300, height: 300 }}>
            <ResponsiveContainer>
                <PieChart>
                    <defs>
                        <linearGradient
                            id="fillColor"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0%"
                                stopColor={gradientStart}
                                stopOpacity={1}
                            />
                            <stop
                                offset="100%"
                                stopColor={gradientEnd}
                                stopOpacity={1}
                            />
                        </linearGradient>
                    </defs>

                    {/* GROW outer ring */}
                    <Pie
                        data={growData}
                        dataKey="value"
                        innerRadius="85%"
                        outerRadius="100%"
                        startAngle={90}
                        endAngle={-270}
                        stroke="none"
                        labelLine={false}
                        label={() =>
                            activeTab === "grow"
                                ? renderCenterLabel("GROW", growProgress)
                                : null
                        }
                        cornerRadius={10}
                    >
                        <Cell
                            fill={
                                activeTab === "grow"
                                    ? "url(#fillColor)"
                                    : "#ccc"
                            }
                        />
                        <Cell
                            fill={
                                activeTab === "grow"
                                    ? leftoverActiveColor
                                    : "#eee"
                            }
                        />
                    </Pie>

                    {/* POST middle ring */}
                    <Pie
                        data={postData}
                        dataKey="value"
                        innerRadius="64%"
                        outerRadius="79%"
                        startAngle={90}
                        endAngle={-270}
                        stroke="none"
                        labelLine={false}
                        label={() =>
                            activeTab === "post"
                                ? renderCenterLabel("POST", postProgress)
                                : null
                        }
                        cornerRadius={10}
                    >
                        <Cell
                            fill={
                                activeTab === "post"
                                    ? "url(#fillColor)"
                                    : "#ccc"
                            }
                        />
                        <Cell
                            fill={
                                activeTab === "post"
                                    ? leftoverActiveColor
                                    : "#eee"
                            }
                        />
                    </Pie>

                    {/* ENGAGE inner ring */}
                    <Pie
                        data={engageData}
                        dataKey="value"
                        innerRadius="43%"
                        outerRadius="58%"
                        startAngle={90}
                        endAngle={-270}
                        stroke="none"
                        labelLine={false}
                        label={() =>
                            activeTab === "engage"
                                ? renderCenterLabel("ENGAGE", engagePercent)
                                : null
                        }
                        cornerRadius={10}
                    >
                        <Cell
                            fill={
                                activeTab === "engage"
                                    ? "url(#fillColor)"
                                    : "#ccc"
                            }
                        />
                        <Cell
                            fill={
                                activeTab === "engage"
                                    ? leftoverActiveColor
                                    : "#eee"
                            }
                        />
                    </Pie>

                    <Tooltip />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
}
